import React, { useState } from 'react';
import { Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { normalizeMedia, debugMediaStructure, NormalizedMedia } from '../../utils/mediaUtils';

interface MediaStructureDebugProps {
  media: any;
  postId?: string;
}

export const MediaStructureDebug: React.FC<MediaStructureDebugProps> = ({ media, postId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const debugInfo = debugMediaStructure(media);
  const normalized = normalizeMedia(media);
  
  const getStructureColor = (structure: string) => {
    switch (structure) {
      case 'firestore-mapValue': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'object-with-url': return 'bg-green-50 text-green-700 border-green-200';
      case 'string': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'array': return 'bg-purple-50 text-purple-700 border-purple-200';
      default: return 'bg-red-50 text-red-700 border-red-200';
    }
  };

  const getStructureIcon = (structure: string) => {
    switch (structure) {
      case 'firestore-mapValue': return '🔄';
      case 'object-with-url': return '✅';
      case 'string': return '📝';
      case 'array': return '📚';
      default: return '❌';
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 text-xs">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-gray-500">Media Debug</span>
          {postId && (
            <span className="text-gray-400 text-[10px]">ID: {postId.slice(0, 8)}</span>
          )}
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-1 hover:bg-gray-100 rounded"
        >
          {isExpanded ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
        </button>
      </div>

      {/* Quick Status */}
      <div className="flex items-center gap-2 mb-2">
        <div className={`px-2 py-1 rounded text-[10px] border ${getStructureColor(debugInfo.structure)}`}>
          {getStructureIcon(debugInfo.structure)} {debugInfo.structure}
        </div>
        <div className={`px-2 py-1 rounded text-[10px] border ${debugInfo.hasUrl ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
          {debugInfo.hasUrl ? <CheckCircle className="w-2 h-2 inline mr-1" /> : <AlertCircle className="w-2 h-2 inline mr-1" />}
          {debugInfo.hasUrl ? 'URL OK' : 'No URL'}
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="space-y-2 pt-2 border-t border-gray-100">
          <div>
            <span className="text-gray-500">Original Type:</span>
            <span className="ml-2 font-mono">{debugInfo.originalType}</span>
          </div>
          
          <div>
            <span className="text-gray-500">Is Array:</span>
            <span className="ml-2">{debugInfo.isArray ? '✅' : '❌'}</span>
          </div>

          {normalized && (
            <div className="bg-gray-50 p-2 rounded">
              <div className="text-gray-500 mb-1">Normalized Result:</div>
              <div className="font-mono text-[10px] space-y-1">
                <div>URL: {normalized.url ? '✅' : '❌'}</div>
                <div>Type: {normalized.type}</div>
                {normalized.fileName && <div>File: {normalized.fileName}</div>}
              </div>
            </div>
          )}

          <div className="bg-gray-50 p-2 rounded">
            <div className="text-gray-500 mb-1">Raw Data:</div>
            <pre className="font-mono text-[10px] overflow-x-auto max-h-20 overflow-y-auto">
              {JSON.stringify(media, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}; 